// IM-EXT-007 — busca enquanto digita: só a partir de N letras, com espera (debounce), cancelamento da requisição anterior
// quando o texto muda, respostas antigas ignoradas e cache curto na sessão (a mesma grafia não sai à rede de novo).
import { useEffect, useRef, useState } from 'react';

export type SearchState<T> =
  | { kind: 'idle' } | { kind: 'short' } | { kind: 'loading'; previous: T | null } | { kind: 'ready'; data: T } | { kind: 'error'; error: Error };

export function useDebouncedSearch<T>(query: string, fetcher: (q: string, signal: AbortSignal) => Promise<T>, opts: { delayMs?: number; minChars?: number; cacheMs?: number; shouldCache?: (data: T) => boolean; nonce?: number } = {}): SearchState<T> {
  const delay = opts.delayMs ?? 400, min = opts.minChars ?? 3, cacheMs = opts.cacheMs ?? 5 * 60_000;
  const [state, setState] = useState<SearchState<T>>({ kind: 'idle' });
  const cache = useRef(new Map<string, { at: number; data: T }>());
  const last = useRef<T | null>(null);
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;
  const cacheRef = useRef(opts.shouldCache);
  cacheRef.current = opts.shouldCache;
  const nonce = opts.nonce ?? 0;
  useEffect(() => {
    const q = query.trim().replace(/\s+/g, ' ');
    if (!q) { setState({ kind: 'idle' }); return; }
    if (q.length < min) { setState({ kind: 'short' }); return; }
    const k = q.toLowerCase();
    const hit = cache.current.get(k);
    if (hit && Date.now() - hit.at < cacheMs && !nonce) { last.current = hit.data; setState({ kind: 'ready', data: hit.data }); return; }
    const ctrl = new AbortController();
    setState({ kind: 'loading', previous: last.current });
    const t = setTimeout(() => {
      fetchRef.current(q, ctrl.signal).then((data) => {
        if (ctrl.signal.aborted) return; // resposta de um texto antigo: ignorada
        // Resultado incompleto (fonte fora do ar, parcial) não entra no cache: a próxima tentativa vai à rede.
        if (cacheRef.current?.(data) ?? true) cache.current.set(k, { at: Date.now(), data }); else cache.current.delete(k);
        last.current = data;
        setState({ kind: 'ready', data });
      }).catch((e: Error) => { if (!ctrl.signal.aborted && e.name !== 'AbortError') setState({ kind: 'error', error: e }); });
    }, delay);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query, delay, min, cacheMs, nonce]);
  return state;
}
